// lib/services/capture/color_camera_slot.ts

import BaseSlot, { SlotState } from "../compute/base_slot";
import { YuvFormat } from "../compute/yuvdefs"

/**
 * Per-frame slot holding GPU-resident YUV textures for one captured camera
 * frame. The capture service uploads YUV plane bytes into these textures
 * once per frame; the sticker compute pipeline reads them as inputs.
 *
 * Lifecycle: Idle -> Recording (capture in progress) -> Ready (frame
 * published) -> Leased (compute consumer reading) -> Ready (release; can be
 * re-leased if no newer slot has been published) -> ... -> Idle on recycle.
 *
 * Pool policy: PostReleasePolicy.KeepReady, matching the animation pool.
 *
 * Textures are allocated once at slot construction and reused across frames.
 * The capture service writes new bytes into the same textures each frame.
 */
export default class ColorCameraSlot extends BaseSlot {
  // NV12: y + cbcr; I420: y + u + v.
  format: YuvFormat = "NV12";

  // Y plane
  y_texture: GPUTexture | null = null;
  y_view: GPUTextureView | null = null;
  y_width: number = 0;
  y_height: number = 0;

  // NV12 interleaved CbCr plane
  cbcr_texture: GPUTexture | null = null;
  cbcr_view: GPUTextureView | null = null;
  cbcr_width: number = 0;
  cbcr_height: number = 0;

  // I420 separate Cb / Cr planes
  u_texture: GPUTexture | null = null;
  u_view: GPUTextureView | null = null;
  u_width: number = 0;
  u_height: number = 0;

  v_texture: GPUTexture | null = null;
  v_view: GPUTextureView | null = null;
  v_width: number = 0;
  v_height: number = 0;

  constructor() {
    super(false);
  }

  /**
   * Allocate the slot's GPU textures. Called once per slot during pool
   * initialization. The slot owns the textures and destroys them on
   * destroy_textures().
   *
   * For NV12 the cbcr_width / cbcr_height should be the half-resolution
   * dimensions of the CbCr plane (one rg8 texel per 2x2 luma block). For
   * I420 the u/v textures share dimensions equal to width/2 x height/2.
   */
  allocate_textures(
    device: GPUDevice,
    key: string,
    slot_index: number,
    format: YuvFormat,
    y_width: number,
    y_height: number,
    chroma_width: number,
    chroma_height: number
  ): void {
    this.format = format;

    this.y_width = y_width;
    this.y_height = y_height;

    this.y_texture = device.createTexture({
      label: `${key}_y_${slot_index}`,
      size: { width: y_width, height: y_height, depthOrArrayLayers: 1 },
      format: "r8unorm",
      usage:
        GPUTextureUsage.TEXTURE_BINDING |
        GPUTextureUsage.COPY_DST,
    });
    this.y_view = this.y_texture.createView();

    if (format === "NV12") {
      this.cbcr_width = chroma_width;
      this.cbcr_height = chroma_height;

      this.cbcr_texture = device.createTexture({
        label: `${key}_cbcr_${slot_index}`,
        size: { width: chroma_width, height: chroma_height, depthOrArrayLayers: 1 },
        format: "rg8unorm",
        usage:
          GPUTextureUsage.TEXTURE_BINDING |
          GPUTextureUsage.COPY_DST,
      });
      this.cbcr_view = this.cbcr_texture.createView();
    } else if (format === "I420") {
      this.u_width = chroma_width;
      this.u_height = chroma_height;
      this.v_width = chroma_width;
      this.v_height = chroma_height;

      this.u_texture = device.createTexture({
        label: `${key}_u_${slot_index}`,
        size: { width: chroma_width, height: chroma_height, depthOrArrayLayers: 1 },
        format: "r8unorm",
        usage:
          GPUTextureUsage.TEXTURE_BINDING |
          GPUTextureUsage.COPY_DST,
      });
      this.u_view = this.u_texture.createView();

      this.v_texture = device.createTexture({
        label: `${key}_v_${slot_index}`,
        size: { width: chroma_width, height: chroma_height, depthOrArrayLayers: 1 },
        format: "r8unorm",
        usage:
          GPUTextureUsage.TEXTURE_BINDING |
          GPUTextureUsage.COPY_DST,
      });
      this.v_view = this.v_texture.createView();
    } else {
      throw new Error(`ColorCameraSlot: unsupported YUV format ${format}`);
    }
  }

  /**
   * Producer entry point: transition Idle -> Recording. Returns false if the
   * slot is not Idle.
   */
  begin_record(): boolean {
    if (this.state !== SlotState.Idle) return false;
    this.transition_to(SlotState.Recording);
    return true;
  }

  /**
   * Producer commit: Recording -> Ready with sequence and timestamp. The
   * pool's publish_slot path normally drives this, but direct transitions
   * are supported too.
   */
  mark_ready(sequence: number, timestamp_sec: number): void {
    this.sequence = sequence;
    this.timestampSec = timestamp_sec;
    this.transition_to(SlotState.Ready);
  }

  /**
   * Producer cancel: Recording -> Idle, dropping the partial frame.
   */
  cancel_record(): void {
    if (this.state === SlotState.Recording) {
      this.transition_to(SlotState.Idle);
    }
  }

  /**
   * Per-frame recycle clears CPU bookkeeping but keeps the GPU textures
   * alive for reuse. Full destruction happens via destroy_textures().
   */
  recycle(): void {
    super.recycle();
  }

  /**
   * Explicit destruction. Called during pool shutdown.
   */
  destroy_textures(): void {
    if (this.y_texture) { this.y_texture.destroy(); this.y_texture = null; }
    this.y_view = null;

    if (this.cbcr_texture) { this.cbcr_texture.destroy(); this.cbcr_texture = null; }
    this.cbcr_view = null;

    if (this.u_texture) { this.u_texture.destroy(); this.u_texture = null; }
    this.u_view = null;

    if (this.v_texture) { this.v_texture.destroy(); this.v_texture = null; }
    this.v_view = null;

    this.y_width = 0;
    this.y_height = 0;
    this.cbcr_width = 0;
    this.cbcr_height = 0;
    this.u_width = 0;
    this.u_height = 0;
    this.v_width = 0;
    this.v_height = 0;
  }
}